import { HEADER_HEIGHT } from '@/constants/dimensions';

// Tunable values for the personalized content feed and the focused "loop".
//
// These live together because most of them are product decisions rather than
// implementation details — dwell thresholds, page sizes and the loop's resting opacity
// are all things a reviewer may want to change without reading component code.

// --- Feed / query ---

export const FEED_PAGE_SIZE = 12;

// Published content is near-static, so it is safe to cache for a while. Thumbnails are
// stable stored URLs (Mux/YouTube/direct), not short-lived signed URLs.
export const CONTENT_STALE_TIME = 5 * 60_000; // no background refetch on revisit
export const CONTENT_GC_TIME = 30 * 60_000; // keep prefetched-but-unclicked items around

// --- Search ---

// The backend enforces this *after* sanitizing MongoDB text-search operators, so `q=a-`
// (which sanitizes to `a`) is rejected. Matching it client-side avoids guaranteed 400s.
export const SEARCH_MIN_LENGTH = 2;
// Backend caps `q` at 100 chars (@MaxLength(100)); beyond it the request 400s.
export const SEARCH_MAX_LENGTH = 100;
export const SEARCH_DEBOUNCE_MS = 300;

// --- Focused-view telemetry ---

// Minimum engagement before an item counts as a view. Low enough not to undercount quick
// real views, high enough to exclude scroll-through.
export const VIEW_THRESHOLD_MS = 2_000;

// Clips shorter than this could never reach the threshold, so they qualify at half their
// duration instead.
export const SHORT_CLIP_MS = 4_000;

// Below this, an item the user moved past is reported as a fast swipe-away.
export const FAST_SWIPE_MS = 500;

// The YouTube IFrame API has no timeupdate event, so playback position is polled.
export const PLAYBACK_POLL_MS = 500;

// --- Loop layout ---

// Portrait media fills the viewport height, giving up only the app header, the page
// template's top margin and the loop's own padding.
export const LOOP_CHROME_OFFSET = HEADER_HEIGHT + 40 + 32;

// Below this the frame stops shrinking and the page scrolls instead — a short window
// should not squash the video into a letterbox.
export const LOOP_PORTRAIT_MIN_HEIGHT = '420px';

// On a very tall window the derived width would crowd out the text column and the rail.
export const LOOP_PORTRAIT_MAX_WIDTH = '560px';

// Landscape reels are width-driven, and this is the design width. The stage centres the
// media between two equal side zones, and the right zone holds the rail (42px) and the
// navigation (38px) — within the 1240px container that puts the hard ceiling near 1008px
// before those controls get squeezed, so this keeps ~150px of slack.
export const LOOP_LANDSCAPE_MAX_WIDTH = 852;

// Room reserved under a landscape video for attribution, title, description and meta.
export const LOOP_LANDSCAPE_META_RESERVE = 170;

// The text column beside portrait media, capped to a comfortable reading measure.
export const LOOP_TEXT_COLUMN_MAX_WIDTH = '340px';

// Start fetching the next page this many items before the end of the loaded stream.
export const LOOP_PREFETCH_MARGIN = 3;

// Anything smaller is an accidental touch rather than a deliberate swipe.
export const LOOP_SWIPE_THRESHOLD_PX = 60;

// --- Loop chrome ---

// Everything around the media rests dimmed so the media holds the focus; whatever the
// viewer points at returns to full strength.
export const LOOP_RESTING_OPACITY = 0.45;

// --- Post card typography ---

// The post body is auto-fitted to the card, between these bounds.
export const POST_BODY_MIN_FONT_SIZE = 13;
export const POST_BODY_MAX_FONT_SIZE = 26;
